import type { ComponentProps, ReactNode } from "react"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { statusDot, statusLabel } from "@/components/steel/status"

export { statusDot, statusLabel }

export function PageHeader({
  title,
  meta,
  actions,
  leading,
}: {
  title: ReactNode
  meta?: ReactNode
  actions?: ReactNode
  leading?: ReactNode
}) {
  return (
    <div className="flex h-[52px] items-center justify-between gap-4 border-b border-border px-6">
      <div className="flex min-w-0 items-center gap-2.5">
        {leading}
        <h1 className="truncate text-head font-semibold tracking-[-0.01em]">{title}</h1>
        {meta ? <span className="truncate text-label text-muted-foreground">{meta}</span> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  )
}

export function KpiStrip({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-flow-col auto-cols-[minmax(190px,1fr)] overflow-x-auto border-b border-border">
      {children}
    </div>
  )
}

export function Kpi({
  label,
  value,
  hint,
  accent,
  compact,
}: {
  label: string
  value: ReactNode
  hint?: ReactNode
  accent?: boolean
  compact?: boolean
}) {
  return (
    <div className={cn("border-r border-border", compact ? "px-5 py-3.5" : "px-5 py-4")}>
      <p className="eyebrow mb-2">{label}</p>
      <p
        className={cn(
          "num font-semibold leading-none tracking-[-0.02em]",
          compact ? "text-[20px]" : "text-[25px]",
          accent && "text-whisper",
        )}
      >
        {value}
      </p>
      {hint ? <p className="mt-1.5 text-meta text-muted-foreground">{hint}</p> : null}
    </div>
  )
}

export function Section({
  title,
  hint,
  action,
  children,
  className,
}: {
  title?: ReactNode
  hint?: ReactNode
  action?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={cn("px-6 py-4", className)}>
      {title ? (
        <div className="mb-3 flex items-baseline justify-between gap-4">
          <div>
            <h2 className="text-title font-semibold">{title}</h2>
            {hint ? <p className="mt-0.5 text-meta text-muted-foreground">{hint}</p> : null}
          </div>
          {action}
        </div>
      ) : null}
      {children}
    </section>
  )
}

export function LinkAction({ children, ...props }: ComponentProps<"button">) {
  return (
    <button
      type="button"
      className="text-label text-muted-foreground transition-colors hover:text-foreground"
      {...props}
    >
      {children}
    </button>
  )
}

export function ViewToggle<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T
  options: { value: T; label: string }[]
  onChange: (next: T) => void
}) {
  return (
    <div className="flex h-7 items-center rounded-md border border-border bg-card p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={cn(
            "h-[22px] rounded-[5px] px-2 text-meta font-medium transition-colors",
            value === option.value ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

export function TileGrid({ min = 210, children }: { min?: number; children: ReactNode }) {
  return (
    <div
      className="grid gap-px overflow-hidden rounded-lg border border-border bg-border"
      style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))` }}
    >
      {children}
    </div>
  )
}

export function Tile({
  label,
  value,
  sub,
  accent,
  children,
}: {
  label: string
  value?: ReactNode
  sub?: ReactNode
  accent?: boolean
  children?: ReactNode
}) {
  return (
    <div className="bg-card px-4 py-3.5">
      <p className="eyebrow mb-1.5">{label}</p>
      {value !== undefined ? (
        <p className={cn("num text-[19px] font-semibold tracking-[-0.02em]", accent && "text-whisper")}>
          {value}
        </p>
      ) : null}
      {sub ? <p className="mt-1 text-meta text-muted-foreground">{sub}</p> : null}
      {children}
    </div>
  )
}

export function StatusBadge({ status, label }: { status: string; label?: string }) {
  return (
    <Badge variant="outline" className={label ? undefined : "capitalize"}>
      <span className="h-[5px] w-[5px] shrink-0 rounded-full" style={{ background: statusDot(status) }} />
      {label ?? statusLabel(status)}
    </Badge>
  )
}

export function Meter({ pct, label }: { pct: number; label?: string }) {
  const clamped = Math.max(0, Math.min(100, pct))
  return (
    <span className="flex items-center gap-2">
      <span className="h-[3px] flex-1 overflow-hidden rounded-full bg-secondary">
        <span className="block h-[3px] bg-foreground" style={{ width: `${clamped}%` }} />
      </span>
      {label !== undefined ? (
        <span className="num w-[34px] text-right text-meta text-muted-foreground">{label}</span>
      ) : null}
    </span>
  )
}

export function Avatar({ initials, size = 26 }: { initials: string; size?: number }) {
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-full bg-secondary text-[10px] font-semibold"
      style={{ height: size, width: size }}
    >
      {initials}
    </span>
  )
}

export function Field({
  label,
  children,
  className,
}: {
  label: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <label className={cn("flex flex-col gap-1", className)}>
      <span className="text-meta text-muted-foreground">{label}</span>
      {children}
    </label>
  )
}
